import { Injectable } from '@angular/core';
import { TableParameters } from 'app/component-library/table/table-parameters';
import { UpdateState } from 'app/shared/update-state-item';
import { ModelStatus } from 'app/viewmodels/model-status';
import { LazyLoadEvent } from 'primeng/api';
import { BehaviorSubject, Subject } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { BackendService } from '../../core/backend.service';
import { ApiResult } from '../../core/messages/api-result';
import { DisplayMessage } from '../../core/messages/display-message';
import { ModelFunctionsService } from '../../shared/model-functions/model-functions.service';
import { EquipmentFunction } from '../../viewmodels/equipment/equipment-function';
import { RunnableModelSearchResult } from '../../viewmodels/equipment/equipment-function-model-search-result';
import { ModelFunctions } from '../../viewmodels/equipment/model-equipment-functions';
import { MaintenanceStrategy } from '../../viewmodels/maintenance-strategies/maintenance-strategy';
import { MaintenanceStrategyAttribute } from '../../viewmodels/maintenance-strategies/maintenance-strategy-attribute';
import { MaintenanceStrategyBoundaryDiagram } from '../../viewmodels/maintenance-strategies/maintenance-strategy-boundary-diagram';
import { MaintenanceStrategyPlannedMaintenance } from '../../viewmodels/maintenance-strategies/maintenance-strategy-planned-maintenance';
import { PermissionsService } from './../../core/permissions.service';
import { MaintenanceStrategyFailureDataService } from './failure-data/maintenance-strategy-failure-data.service';
import { BoundaryDiagramService } from './header/boundary-diagram.service';
import { MaintenanceStrategyAttributesService } from './header/maintenance-strategy-attributes.service';
import { MaintenanceStrategyPlannedMaintenanceService } from './planned-maintenance/maintenance-strategy-planned-maintenance.service';
import { MaintenanceStrategyPlannedMaintenanceTableConfigService } from './planned-maintenance/table/maintenance-strategy-planned-maintenance-table-config.service';

@Injectable({
  providedIn: 'root',
})
export class MaintenanceStrategyDetailsService {
  public maintenanceStrategy = new BehaviorSubject<MaintenanceStrategy>(null);
  public allowSave = new Subject<boolean>();
  public commitControls = new Subject<boolean>();
  public saveRequested = new Subject();
  public createCompleted = new Subject<MaintenanceStrategy>();
  public updateCompleted = new Subject<MaintenanceStrategy>();
  public messages = new Subject<DisplayMessage[]>();
  public selectedItemIsEditable: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public createPermitted: boolean = false;
  public updatePermitted: boolean = false;
  public hasChanges = new BehaviorSubject<boolean>(false);
  private currentValueLoadEvent: LazyLoadEvent;
  public currentPageRows = new BehaviorSubject<MaintenanceStrategyPlannedMaintenance[]>([]);

  private get strategyId(): string {
    return this.maintenanceStrategy.value && this.maintenanceStrategy.value.id;
  }

  private boundaryDiagram: MaintenanceStrategyBoundaryDiagram;

  constructor(
    private backendService: BackendService,
    private attributeService: MaintenanceStrategyAttributesService,
    private plannedMaintenanceService: MaintenanceStrategyPlannedMaintenanceService,
    private failureDataService: MaintenanceStrategyFailureDataService,
    private modelFunctionsService: ModelFunctionsService,
    private boundaryDiagramService: BoundaryDiagramService,
    private permissionsService: PermissionsService,
    private tableConfigService: MaintenanceStrategyPlannedMaintenanceTableConfigService
  ) {
    this.attributeService.acceptedAddedAttribute.subscribe(this.addAttribute.bind(this));

    this.attributeService.acceptedEditedAttribute.subscribe(this.editAttribute.bind(this));

    this.attributeService.deletedAttributes.subscribe(this.deleteAttributes.bind(this));

    this.plannedMaintenanceService.acceptedAddedMaintenance.subscribe(this.addPlannedMaintenance.bind(this));

    this.plannedMaintenanceService.deletedMaintenance.subscribe(this.deletePlannedMaintenance.bind(this));

    this.failureDataService.modelSelected.subscribe(this.setModel.bind(this));

    this.boundaryDiagramService.boundaryDiagram.subscribe(boundaryDiagram => this.boundaryDiagram = boundaryDiagram);

    this.boundaryDiagramService.errorMessages.subscribe(message => this.messages.next([message]));

    this.permissionsService.userPermissionKeyValuePairs.subscribe(this.setAccessVariables.bind(this));
  }

  private setAccessVariables(): void {
    const {
      adminMaintenanceMaintenanceStrategyCreate,
      adminMaintenanceMaintenanceStrategyUpdate,
    } = this.permissionsService.userPermissionKeys;

    this.createPermitted = this.permissionsService.checkUserHasPermissions(adminMaintenanceMaintenanceStrategyCreate);
    this.updatePermitted = this.permissionsService.checkUserHasPermissions(adminMaintenanceMaintenanceStrategyUpdate);
  }

  public loadNewMaintenanceStrategy(): void {
    const maintenanceStrategy = new MaintenanceStrategy();
    this.maintenanceStrategy.next(maintenanceStrategy);
    this.setEquipmentFunctions(maintenanceStrategy.equipmentFunctions);
    this.boundaryDiagramService.boundaryDiagram.next(null);
    this.selectedItemIsEditable.next(this.createPermitted);
    this.hasChanges.next(false);
  }

  public loadMaintenanceStrategy(maintenanceStrategyId: string): void {
    this.backendService.getById<MaintenanceStrategy>(MaintenanceStrategy, maintenanceStrategyId).subscribe(
      maintenanceStrategy => {
        if (maintenanceStrategy) {
          this.maintenanceStrategy.next(maintenanceStrategy);
          this.setEquipmentFunctions(maintenanceStrategy.equipmentFunctions);
          this.loadBoundaryDiagram(maintenanceStrategy);

          const canEdit: boolean = maintenanceStrategy.status === ModelStatus.Draft && this.updatePermitted;
          this.selectedItemIsEditable.next(canEdit);
          this.hasChanges.next(false);
        }
      },
      (errorResult: ApiResult) => {
        this.emitErrors('Load Maintenance Strategy.', errorResult);
      }
    );
  }

  public saveMaintenanceStrategy(maintenanceStrategy: MaintenanceStrategy): void {
    this.commitControls.next();

    if (maintenanceStrategy.id) {
      this.backendService.update<MaintenanceStrategy>(MaintenanceStrategy, maintenanceStrategy.id, maintenanceStrategy).subscribe(
        strategy => {
          this.updateCompleted.next(strategy);
          this.saveBoundaryDiagram(strategy, this.boundaryDiagram);
          this.setUnchangedState(this.maintenanceStrategy.value);
        },
        (errorResult: ApiResult) => {
          this.emitErrors('Save Maintenance Strategy.', errorResult);
        }
      );
    } else {
      this.backendService.create<MaintenanceStrategy>(MaintenanceStrategy, maintenanceStrategy).subscribe(
        strategy => {
          this.createCompleted.next(strategy);
          this.saveBoundaryDiagram(strategy, this.boundaryDiagram);
          this.setUnchangedState(this.maintenanceStrategy.value);
        },
        (errorResult: ApiResult) => {
          this.emitErrors('Save Maintenance Strategy.', errorResult);
        }
      );
    }
  }

  private setUnchangedState(strategy: MaintenanceStrategy): void {
    this.hasChanges.next(false);
    if (!strategy) return;
    if (!strategy.plannedMaintenance || strategy.plannedMaintenance.length === 0) return;

    strategy.plannedMaintenance.forEach(item => item.state = UpdateState.Unchanged);
  }

  public setModel(searchResult: RunnableModelSearchResult): void {
    if (!this.maintenanceStrategy.value) {
      return;
    }

    this.maintenanceStrategy.value.modelId = searchResult.modelId;
    this.maintenanceStrategy.value.modelName = searchResult.modelName;
    this.maintenanceStrategy.value.equipmentFunctions = searchResult.functions;

    this.setEquipmentFunctions(searchResult.functions);
  }

  private emitErrors(summary: string, errorResult: ApiResult) {
    const messages: DisplayMessage[] = [];

    errorResult.messages.forEach(message => {
      messages.push(new DisplayMessage(summary, message.text, message.severity));
    });

    this.messages.next(messages);
  }

  private addAttribute(addedAttribute: MaintenanceStrategyAttribute): void {
    if (!this.maintenanceStrategy.value || addedAttribute == null) return;

    const attributes = this.maintenanceStrategy.value.attributes;
    if (!attributes || attributes.find(attribute => attribute === addedAttribute)) return;

    const maxRowNumber = this.getMaxCollectionRowNumber(attributes);

    addedAttribute.state = UpdateState.Added;
    addedAttribute.maintenanceStrategyId = this.maintenanceStrategy.value.id;
    addedAttribute.rowNumber = maxRowNumber + 1;

    attributes.push(addedAttribute);
    this.maintenanceStrategy.value.attributes = attributes.slice();

    this.hasChanges.next(true);
  }

  private getMaxCollectionRowNumber = (attributes: { rowNumber: number }[]): number => Math.max(...attributes.map(a => a.rowNumber), 0);

  private editAttribute(editedAttribute: MaintenanceStrategyAttribute): void {
    if (!this.maintenanceStrategy.value || !editedAttribute) return;

    editedAttribute.state = UpdateState.Modified;
    this.hasChanges.next(true);
  }

  private deleteAttributes(deletedAttributes: MaintenanceStrategyAttribute[]): void {
    if (!this.maintenanceStrategy.value) return;

    let attributes = this.maintenanceStrategy.value.attributes
      .filter(attribute => !deletedAttributes.find(deleted => attribute === deleted));

    if (attributes.length > 0) {
      attributes = attributes
        .sort(a => a.rowNumber)
        .map((a, i) => {
          a.rowNumber = i + 1;
          return a;
        });
    }

    this.maintenanceStrategy.value.attributes = attributes;
    this.hasChanges.next(true);
  }

  private addPlannedMaintenance(addedPlannedMaintenance: MaintenanceStrategyPlannedMaintenance[]): void {
    if (!this.maintenanceStrategy.value || addedPlannedMaintenance == null) {
      return;
    }
    const plannedMaintenance = this.maintenanceStrategy.value.plannedMaintenance;
    const maxRowNumber = this.getMaxCollectionRowNumber(plannedMaintenance);
    addedPlannedMaintenance.forEach((addedMaintenance, i) => {
      const exists = plannedMaintenance.find(maintenance =>
        maintenance === addedMaintenance || maintenance.plannedMaintenanceLibraryItemId === addedMaintenance.plannedMaintenanceLibraryItemId
      );
      if (!exists) {
        addedMaintenance.maintenanceStrategyId = this.maintenanceStrategy.value.id;
        addedMaintenance.rowNumber = i + maxRowNumber + 1;
        addedMaintenance.state = UpdateState.Added;
        plannedMaintenance.push(addedMaintenance);
      }
    });

    this.maintenanceStrategy.value.plannedMaintenance = plannedMaintenance.slice();
    this.hasChanges.next(true);
  }

  private deletePlannedMaintenance(deletedPlannedMaintenance: MaintenanceStrategyPlannedMaintenance[]): void {
    if (!this.maintenanceStrategy.value) {
      return;
    }

    let plannedMaintenance = this.maintenanceStrategy.value.plannedMaintenance.filter(
      maintenance => !deletedPlannedMaintenance.find(deleted => deleted === maintenance)
    );

    if (plannedMaintenance.length > 0) {
      plannedMaintenance = plannedMaintenance
        .sort(a => a.rowNumber)
        .map((a, i) => {
          a.rowNumber = i + 1;
          return a;
        });
    }

    this.maintenanceStrategy.value.plannedMaintenance = plannedMaintenance;
    this.hasChanges.next(true);
    this.modelFunctionsService.plannedMaintenanceDeleted.next();
  }

  private setEquipmentFunctions(equipmentFunctions: EquipmentFunction[]): void {
    const modelFunctions = new ModelFunctions();
    modelFunctions.functions = equipmentFunctions;
    this.modelFunctionsService.modelFunctions.next(modelFunctions);
  }

  private loadBoundaryDiagram(maintenanceStrategy: MaintenanceStrategy): void {
    if (!maintenanceStrategy.boundaryDiagramId) {
      this.boundaryDiagramService.boundaryDiagram.next(null);
      return;
    }

    this.backendService.getById<MaintenanceStrategyBoundaryDiagram>(MaintenanceStrategyBoundaryDiagram, maintenanceStrategy.id).subscribe(
      boundaryDiagram => {
        if (boundaryDiagram) {
          this.boundaryDiagramService.boundaryDiagram.next(boundaryDiagram);
        } else {
          this.boundaryDiagramService.boundaryDiagram.next(null);
        }
      },
      (errorResult: ApiResult) => {
        this.emitErrors('Load Boundary Diagram.', errorResult);
        this.boundaryDiagramService.boundaryDiagram.next(null);
      }
    );
  }

  private saveBoundaryDiagram(maintenanceStrategy: MaintenanceStrategy, boundaryDiagram: MaintenanceStrategyBoundaryDiagram): void {
    if (boundaryDiagram == null) {
      return;
    }

    if (!boundaryDiagram.maintenanceStrategyId) {
      boundaryDiagram.maintenanceStrategyId = maintenanceStrategy.id;
    } else if (boundaryDiagram.maintenanceStrategyId !== maintenanceStrategy.id) {
      return;
    }

    if (maintenanceStrategy.boundaryDiagramId) {
      boundaryDiagram.maintenanceStrategyBoundaryDiagramId = maintenanceStrategy.boundaryDiagramId;
      this.backendService
        .update<MaintenanceStrategyBoundaryDiagram>(MaintenanceStrategyBoundaryDiagram, maintenanceStrategy.id, boundaryDiagram)
        .subscribe(
          result => {
            if (result) {
            }
          },
          (errorResult: ApiResult) => {
            this.emitErrors('Save Boundary Diagram.', errorResult);
          }
        );
    } else {
      this.backendService.create<MaintenanceStrategyBoundaryDiagram>(MaintenanceStrategyBoundaryDiagram, boundaryDiagram).subscribe(
        result => {
          if (result) {
            maintenanceStrategy.boundaryDiagramId = result.maintenanceStrategyBoundaryDiagramId;
          }
        },
        (errorResult: ApiResult) => {
          this.emitErrors('Save Boundary Diagram.', errorResult);
        }
      );
    }
  }

  public loadAvailableMaintenanceLazy(event: LazyLoadEvent): void {
    this.currentValueLoadEvent = event;
    this.loadCurrentPageAvailablePlannedMaintenance();
    this.loadAvailablePlannedMaintenanceCount();
  }

  private createAvailableItemsTableParameters(): TableParameters {
    return this.tableConfigService.createAvailableItemsTableParameters(this.currentValueLoadEvent, this.strategyId, this.maintenanceStrategy.value.plannedMaintenance);
  }

  public loadAvailablePlannedMaintenanceCount(): void {
    if (!this.maintenanceStrategy.value) return;

    const tableParams = this.createAvailableItemsTableParameters();
    this.plannedMaintenanceService.loadAvailableItemsCount(tableParams)
      .pipe()
      .subscribe(totalRecords => {
        this.tableConfigService.setAvailableItemsRecordCountConfigParameters(tableParams, totalRecords);
      });
  }

  private loadCurrentPageAvailablePlannedMaintenance(): void {
    if (!this.strategyId) return;

    const tableParams = this.createAvailableItemsTableParameters();
    this.startLoading();

    this.plannedMaintenanceService.paginationLoadCurrentPageAvailableItems(tableParams)
      .pipe(finalize(this.finishLoading.bind(this)))
      .subscribe(pageRows => {
        const processedPageRows = pageRows
          ? this.plannedMaintenanceService.createAvailablePlannedMaintenanceItems(pageRows)
          : [];
        this.currentPageRows.next(processedPageRows);
      });
  }

  public startLoading(): void {
    this.tableConfigService.startLoading();
  }

  public finishLoading(): void {
    this.tableConfigService.finishLoading();
  }
}
